import './ConfiguratorWizard.css';

import { useEffect, useState } from 'react';

import { fixedGripSize, gripTypeData } from '../../data/data';
import { OthersInfo } from '../../interfaces';
import { TranslationKey } from '../../translations/translations';
import {
  getAvailableBrand,
  getAvailableGripSizes,
  getAvailablePipesBySportAndGrip,
  getAvailableSportByProduct,
  getCompatibleHandles,
  shouldShowGripSizeStep,
} from '../../utils/configuratorLogic';
import { t } from '../../utils/translate';
import { Product } from '../ProductCard/ProductCard';
import ProductSelector from '../ProductSelector/ProductSelector';
import StepSelector from '../StepSelector';
import { useConfiguratorContext } from './ConfiguratorContext';

interface ConfiguratorWizardProps {
  onComplete: (product: Product, info: OthersInfo) => void;
  onShowConfigurator: (val: boolean) => void;
}

export default function ConfiguratorWizard({
  onComplete,
  onShowConfigurator,
}: ConfiguratorWizardProps) {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const { sport, setSport, handle, setHandle, pipeSize, setPipeSize, lang } =
    useConfiguratorContext();

  const [brand, setBrand] = useState<string | null>(null);
  const [gripSize, setGripSize] = useState<string | null>(null);
  const [gripType, setGripType] = useState<string | null>(null);

  useEffect(() => {
    if (selectedProduct) {
      onComplete(
        { ...selectedProduct },
        {
          sport: sport!,
          brand: brand!,
          handle: handle!,
          gripSize: gripSize!,
          gripType: gripType!,
          pipeSize: pipeSize!,
        },
      );

      window.parent.postMessage(
        {
          type: 'zakeke-select',
          modelName: selectedProduct.name,
          modelCode: selectedProduct.code,
        },
        '*',
      );
    }
  }, [selectedProduct, sport, brand, handle, gripSize, gripType, pipeSize]);

  useEffect(() => {
    const allSelected = [
      sport,
      brand,
      handle,
      gripSize,
      gripType,
      pipeSize,
    ].every(Boolean);

    onShowConfigurator(allSelected && !!selectedProduct);
  }, [sport, brand, handle, gripSize, gripType, pipeSize, selectedProduct]);

  const steps = [
    () => setSport(null),
    () => setHandle(null),
    () => setBrand(null),
    () => setGripSize(null),
    () => setGripType(null),
    () => setPipeSize(null),
  ];

  const resetFrom = (level: number) => {
    for (let i = level - 1; i < steps.length; i++) {
      steps[i]();
    }
  };

  const handleProductSelected = (product: Product) => {
    setSelectedProduct(product);
    resetFrom(1);
  };

  return (
    <div className="configurator-wizard">
      <ProductSelector onProductSelected={handleProductSelected} />

      {selectedProduct && (
        <div className="step-container">
          {/* Step 1 */}
          <StepSelector
            title={t(lang, 'select_sport_title')}
            placeholder={t(lang, 'select_sport_placeholder')}
            value={sport}
            options={
              selectedProduct
                ? getAvailableSportByProduct(selectedProduct.code)
                : []
            }
            onChange={(val) => {
              setSport(val);
            }}
          />

          {/* Step 2 */}
          <StepSelector
            title={t(lang, 'select_model_title')}
            placeholder={t(lang, 'select_model_placeholder')}
            value={brand}
            options={
              selectedProduct ? getAvailableBrand(selectedProduct.code) : []
            }
            onChange={(val) => {
              setBrand(val);
            }}
          />

          {/* Step 3 */}
          <StepSelector
            title={t(lang, 'select_handle_title')}
            placeholder={t(lang, 'select_handle_placeholder')}
            description={
              !brand ? t(lang, 'select_handle_description_no_brand') : null
            }
            value={handle}
            disabled={!brand}
            options={
              selectedProduct
                ? getCompatibleHandles(selectedProduct.code, brand!)
                : []
            }
            onChange={(val) => {
              setHandle(val);

              const sizes = getAvailableGripSizes(val);

              if (sizes.length === 1 && fixedGripSize.includes(sizes[0])) {
                setGripSize(t(lang, sizes[0] as TranslationKey));
              } else {
                setGripSize(null);
              }
            }}
          />

          {/* Step 4 */}
          <StepSelector
            title={t(lang, 'select_grip_size_title')}
            placeholder={t(lang, 'select_grip_size_placeholder')}
            description={
              gripSize && !shouldShowGripSizeStep(gripSize)
                ? t(lang, 'select_grip_size_description_disabled')
                : !handle
                  ? t(lang, 'select_grip_size_description_no_handle')
                  : null
            }
            value={gripSize}
            disabled={
              !handle ||
              (gripSize !== null && !shouldShowGripSizeStep(gripSize))
            }
            options={
              handle
                ? getAvailableGripSizes(handle).map((key) =>
                    t(lang, key as TranslationKey),
                  )
                : []
            }
            onChange={(label) => {
              const originalKey = getAvailableGripSizes(handle!).find(
                (key) => t(lang, key as TranslationKey) === label,
              );

              if (originalKey) {
                setGripSize(t(lang, originalKey as TranslationKey));
                // Reset pipe size quando cambia grip size
                setPipeSize(null);
              }
            }}
          />
          {/* Step 5 */}
          <StepSelector
            title={t(lang, 'select_grip_type_title')}
            placeholder={t(lang, 'select_grip_type_placeholder')}
            value={gripType}
            options={gripTypeData.map((key) => t(lang, key as TranslationKey))}
            onChange={(label) => {
              const originalKey = gripTypeData.find(
                (key) => t(lang, key as TranslationKey) === label,
              );

              if (originalKey) {
                setGripType(t(lang, originalKey as TranslationKey));
              }
            }}
          />

          {/* Step 6 */}
          <StepSelector
            title={t(lang, 'select_pipe_size_title')}
            placeholder={t(lang, 'select_pipe_size_placeholder')}
            value={pipeSize}
            disabled={!handle || !gripSize}
            //  options={
            //    selectedProduct
            //      ? getAvailablePipesByProduct(selectedProduct.code)
            //      : []
            //  }
            options={
              gripSize
                ? getAvailablePipesBySportAndGrip(
                    selectedProduct.code,
                    gripSize,
                  )
                : []
            }
            description={
              !handle || !gripSize
                ? t(lang, 'select_pipe_size_description_no_handle_or_grip_size')
                : null
            }
            onChange={(val) => setPipeSize(val)}
          />
        </div>
      )}
    </div>
  );
}
